import { Injectable } from '@angular/core';
import { CURRENT_PLATFORM } from './env.service';
import { Platform } from '../models/platform.enum';
import { App } from '@capacitor/app';
import { environment } from '../../environments/environment';
import { ShadeToolsService } from './shade-tools.service';
import { FeatureFlagsService, FeatureFlag } from './feature-flags.service';

@Injectable({
  providedIn: 'root',
})
export class UpdateNotificationService {
  constructor(
    private featureFlagsService: FeatureFlagsService,
    private shadeToolsService: ShadeToolsService
  ) {}


  /**
   * Check if an update is required based on remote config settings
   */
  public async checkForAppUpdates(): Promise<void> {
    if (CURRENT_PLATFORM !== Platform.WEB) {
      try {
        // Get update parameters from Remote Config
        const updateVersion = this.featureFlagsService.getStringValue(CURRENT_PLATFORM === Platform.IOS ? FeatureFlag.IOS_UPDATE_VERSION : FeatureFlag.ANDROID_UPDATE_VERSION);
        const updateMessage = this.featureFlagsService.getStringValue(CURRENT_PLATFORM === Platform.IOS ? FeatureFlag.IOS_UPDATE_MESSAGE : FeatureFlag.ANDROID_UPDATE_MESSAGE);

        const currentVersion = await this.getCurrentAppVersion();
        const updateRequired = this.isUpdateRequired(currentVersion, updateVersion);
        if (updateRequired) {
          this.shadeToolsService.updateAppDialog(updateMessage, environment.storeUrl);
        }
      } catch (error) {
        console.error('Error checking for updates:', error);
      }
    }
  }

  /**
   * Get the current app version
   */
  public async getCurrentAppVersion(): Promise<string> {
    try {
      const appInfo = await App.getInfo();
      return appInfo.version;
    } catch (error) {
      console.error('Error getting app version:', error);
      return null;
    }
  }

  /**
   * Parse a version string into its components
   */
  private parseVersion(version: string): { major: number; minor: number; patch: number } {
    const [major, minor, patch] = version.split('.').map(Number);
    return { major, minor, patch };
  }

  /**
   * Check if an update is required by comparing versions
   */
  private isUpdateRequired(currentVersion: string, minimumVersion: string): boolean {
    const current = this.parseVersion(currentVersion);
    const minimum = this.parseVersion(minimumVersion);

    if (current.major < minimum.major) return true;
    if (current.major > minimum.major) return false;
    if (current.minor < minimum.minor) return true;
    if (current.minor > minimum.minor) return false;
    return current.patch < minimum.patch;
  }
}
